import { styled } from '@mui/joy';
import Button from '@mui/joy/Button';
import SvgIcon from '@mui/joy/SvgIcon';

const VisuallyHiddenInput = styled('input')`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  left: 0;
  white-space: nowrap;
  width: 1px;
`;

type FileUploadButtonProps = {
  /**
   * Called once per selected file. With `multiple`, a picker selection of
   * several files fires this for each of them, in the order chosen.
   */
  onSelect: (file: File) => void;
  disabled?: boolean;
  accept?: string;
  /**
   * Allow selecting several files in one go. Off by default: the banner-image
   * and library-upload callers each bind a single File and would silently
   * keep only the last one. Post attachments opt in.
   */
  multiple?: boolean;
};

export const FileUploadButton = (props: FileUploadButtonProps) => {
  return (
    <Button
      component="label"
      role={undefined}
      tabIndex={-1}
      variant="outlined"
      color="neutral"
      disabled={props.disabled}
      startDecorator={
        <SvgIcon>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z"
            />
          </svg>
        </SvgIcon>
      }
    >
      Selecteer een bestand
      <VisuallyHiddenInput
        type="file"
        accept={props.accept ?? '*'}
        multiple={props.multiple ?? false}
        onChange={(event) => {
          const input = event.target as HTMLInputElement;
          // Previously read files[0] only, with no `multiple` attribute, so a
          // multi-file selection silently attached just the first — no error,
          // no hint that the rest were dropped.
          Array.from(input.files ?? []).forEach((file) => props.onSelect(file));

          // Clear the input so picking the SAME file again still fires
          // `change`. Without this, removing an attachment and re-adding the
          // identical file appears to do nothing.
          input.value = '';
        }}
      />
    </Button>
  );
};
